import {
  type ButtonHTMLAttributes,
  type DragEventHandler,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { useRenameField } from '@/components/ui/RenameField/useRenameField';
import {
  ROUND_ICON_BUTTON_EDGE_INSET,
  RoundIconButton,
} from '@/design-system-v2/round-icon-button';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { cn } from '@/lib/utils';
import { Panel } from '@/stores/ui.store';
import { trackEvent } from '@/utils/analytics';
import { isMac } from '@/utils/capabilities';

import { SidebarItemMenuItems } from './SidebarItemMenuItems';

const getItemId = (item: unknown): string | null => {
  if (!item || typeof item !== 'object' || !('id' in item)) return null;
  return typeof item.id === 'string' ? item.id : null;
};

export const SidebarMoreActionsButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => (
  <RoundIconButton
    ref={ref}
    icon={Icons.moreHorizontal}
    label="More actions"
    size="xs"
    className={cn('-my-0.5', ROUND_ICON_BUTTON_EDGE_INSET.xs, className)}
    {...props}
  />
));

SidebarMoreActionsButton.displayName = 'SidebarMoreActionsButton';

interface SidebarSectionRowProps<T> {
  item: T;
  title: string;
  subtitle?: string;
  isSelected?: boolean;
  onSelect: (item: T, e?: React.MouseEvent) => void;
  onClick?: (item: T) => void;
  onOpenInTab?: (item: T) => void;
  onPrewarm?: (item: T) => void;
  onRename?: (item: T, ...args: string[]) => void;
  onFavorite?: (item: T) => void;
  onMarkUnread?: (item: T) => void;
  onArchive?: (item: T) => void;
  onDelete?: (item: T) => void;
  isFavorited?: boolean | null;
  contextMenuItems?: ReactNode;
  contextMenuExtra?: ReactNode;
  dropdownMenuExtra?: ReactNode;
  menuContentClassName?: string;
  panel: Panel;
  className?: string;
  muted?: boolean;
  isLoading?: boolean;
  showLeading?: boolean;
  leftSide?: ReactNode | null;
  leadingUnread?: boolean;
  rightSide?: ReactNode | null;
  draggable?: boolean;
  onDragStart?: DragEventHandler<HTMLDivElement>;
}

export function SidebarSectionRow<T>({
  item,
  title,
  subtitle,
  isSelected,
  onSelect,
  onClick,
  onOpenInTab,
  onPrewarm,
  onRename,
  onFavorite,
  onMarkUnread,
  onArchive,
  onDelete,
  isFavorited,
  contextMenuItems,
  contextMenuExtra,
  dropdownMenuExtra,
  menuContentClassName,
  className,
  muted,
  panel,
  isLoading = false,
  showLeading = true,
  leftSide,
  leadingUnread,
  rightSide,
  draggable = false,
  onDragStart,
}: SidebarSectionRowProps<T> & { panel: Panel }) {
  const { renameFieldRef, showRenameInputField } = useRenameField();
  const skipClickAfterDragRef = useRef(false);

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuResetKey, setContextMenuResetKey] = useState(0);
  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);

  const handleContextMenuOpenChange = useCallback((open: boolean) => {
    setContextMenuOpen(open);
    if (open) setDropdownMenuOpen(false);
  }, []);

  const handleDropdownMenuOpenChange = useCallback(
    (open: boolean) => {
      setDropdownMenuOpen(open);
      if (open && contextMenuOpen) {
        setContextMenuOpen(false);
        setContextMenuResetKey((key) => key + 1);
      }
    },
    [contextMenuOpen]
  );

  useEffect(() => {
    if (!dropdownMenuOpen) return;
    const closeMenu = () => setDropdownMenuOpen(false);
    window.addEventListener('blur', closeMenu);
    return () => window.removeEventListener('blur', closeMenu);
  }, [dropdownMenuOpen]);

  const handleFavorite = useCallback(() => {
    onFavorite?.(item);
    const itemId = getItemId(item);
    if (panel === Panel.Chats && itemId) {
      trackEvent('CHAT_STARRED', {
        chatId: itemId,
        favorite: !isFavorited,
      });
    }
  }, [onFavorite, item, isFavorited, panel]);

  const handleClick = useCallback(
    (e?: React.MouseEvent) => {
      onSelect(item, e);
      onClick?.(item);
    },
    [onSelect, item, onClick]
  );

  const handleRename = useCallback(
    (title: string) => {
      onRename?.(item, title);
      const itemId = getItemId(item);
      if (panel === Panel.Chats && itemId) {
        trackEvent('CHAT_TITLE_RENAMED', {
          chatId: itemId,
        });
      }
    },
    [item, onRename, panel]
  );

  const handleDragStart: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      skipClickAfterDragRef.current = true;
      onDragStart?.(event);
    },
    [onDragStart]
  );

  const handlePrewarm = useCallback(() => {
    onPrewarm?.(item);
  }, [item, onPrewarm]);

  const menuActions = {
    title,
    isFavorited,
    onOpenInTab: onOpenInTab ? () => onOpenInTab(item) : undefined,
    onRename: onRename
      ? () => {
          setTimeout(() => {
            showRenameInputField();
          }, 0);
        }
      : undefined,
    onFavorite: onFavorite ? handleFavorite : undefined,
    onMarkUnread: onMarkUnread ? () => onMarkUnread(item) : undefined,
    onArchive: onArchive ? () => onArchive(item) : undefined,
    onDelete: onDelete ? () => onDelete(item) : undefined,
  };

  const hasActionHandlers = !!(
    menuActions.onOpenInTab ||
    menuActions.onRename ||
    menuActions.onFavorite ||
    menuActions.onMarkUnread ||
    menuActions.onArchive ||
    menuActions.onDelete
  );
  const hasInlineActions =
    hasActionHandlers || !!contextMenuExtra || !!dropdownMenuExtra;
  const showMoreButton = !contextMenuItems && hasInlineActions;

  return (
    <ContextMenu
      key={contextMenuResetKey}
      onOpenChange={handleContextMenuOpenChange}
    >
      <ContextMenuTrigger
        className="group/context-menu"
        disabled={!contextMenuItems && !hasActionHandlers && !contextMenuExtra}
        aria-label={`${title}${subtitle ? `, ${subtitle}` : ''}`}
        aria-selected={isSelected}
        draggable={draggable}
        onDragStart={handleDragStart}
      >
        <SidebarRow.Root
          selected={isSelected}
          muted={muted}
          className={cn(
            'group',
            !isSelected &&
              'group-data-[state=open]/context-menu:bg-surface-hover group-has-[[data-state=open]]:bg-surface-hover',
            className
          )}
          onMouseDown={(e) => {
            if (draggable) return;
            if (e.button === 0 && !(isMac() && e.ctrlKey)) handleClick(e);
          }}
          onPointerEnter={handlePrewarm}
          onFocus={(e) => {
            if (e.target === e.currentTarget) handlePrewarm();
          }}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          onClick={(e) => {
            if (skipClickAfterDragRef.current) {
              skipClickAfterDragRef.current = false;
              return;
            }
            if (draggable || e.detail === 0) handleClick(e);
          }}
          role="button"
          tabIndex={0}
          aria-label={`Select ${title}${subtitle ? `, ${subtitle}` : ''}`}
          aria-selected={isSelected}
        >
          {showLeading && (
            <SidebarRow.Leading unread={leadingUnread}>
              {leftSide}
            </SidebarRow.Leading>
          )}
          <YStack className="flex-1 min-w-0">
            <XStack className="items-center gap-1">
              <p className="truncate min-w-0 relative flex-1">
                <RenameField
                  ref={renameFieldRef}
                  value={title}
                  onRename={handleRename}
                  isLoading={isLoading}
                />
              </p>

              {rightSide && (
                <span
                  className={cn(
                    'inline-flex overflow-hidden',
                    showMoreButton &&
                      'group-hover:w-0 group-has-[[data-state=open]]:w-0'
                  )}
                >
                  {rightSide}
                </span>
              )}
            </XStack>
            {subtitle && (
              <p
                className={cn(
                  'text-foreground-muted',
                  'text-xs',
                  'truncate',
                  'w-full'
                )}
              >
                {subtitle}
              </p>
            )}
          </YStack>
          {showMoreButton && (
            <DropdownMenu.Root
              open={dropdownMenuOpen}
              onOpenChange={handleDropdownMenuOpenChange}
            >
              <DropdownMenu.Trigger asChild>
                <SidebarMoreActionsButton
                  aria-label={`More actions for ${title}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    // touch has no hover, so the row actions menu is always
                    // reachable there
                    'hidden touch:flex group-hover:flex',
                    'group-has-[:focus-visible]:flex',
                    'data-[state=open]:flex'
                  )}
                />
              </DropdownMenu.Trigger>
              {dropdownMenuOpen && (
                <DropdownMenu.Content
                  align="start"
                  alignOffset={-15}
                  className={menuContentClassName}
                  fit
                  aria-label={`Actions for ${title}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SidebarItemMenuItems
                    variant="dropdown"
                    {...menuActions}
                    extra={dropdownMenuExtra}
                  />
                </DropdownMenu.Content>
              )}
            </DropdownMenu.Root>
          )}
        </SidebarRow.Root>
      </ContextMenuTrigger>
      {contextMenuOpen && (
        <ContextMenuContent
          role="menu"
          aria-label={`Context menu for ${title}`}
          className={menuContentClassName}
        >
          {contextMenuItems || (
            <SidebarItemMenuItems
              variant="context"
              {...menuActions}
              extra={contextMenuExtra}
            />
          )}
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}
